/**
 * The "Ask" tab — Fluent v9 rendition of references/ui-spec.md §5 and the decision tree in
 * references/ask-catalog.md §4.
 *
 * Renders reviewed catalog entries verbatim; nothing here composes an answer. "Not found" is shown
 * as itself, with the nearest jobs offered only as searches to try. "Take me there" navigates AND
 * flashes the exact control (opening its dialog when the entry says `anchor_action: open`).
 *
 * PCT answers in the browser (`ask-api.ts` → `ask/ask-client.ts`), so there is no request to
 * fail in the usual sense; the cache below keeps the template's rule anyway: an ANSWER is reused
 * for 10 minutes, a MISS never is (every repeated miss must reach the miss log).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Input, Spinner } from '@fluentui/react-components';
import {
  ArrowRight16Regular,
  Open16Regular,
  Search16Regular,
  Target16Regular,
  ThumbDislike16Regular,
  ThumbLike16Regular,
} from '@fluentui/react-icons';
import { searchAssistant, sendAssistantFeedback, type AssistantResultCard, type AssistantSearchResponse } from './ask-api';
import { flashAnchor, type MissReason, type YieldHandler } from './dom';
import type { HelpNotice } from './HelpLauncher';
import { matchesRoute } from './registry';
import { HELP_STRINGS, type HelpStrings } from './strings';
import type { HelpLanguage } from './types';

/** Shorter is a word in the making, sent because the debounce fired between keystrokes. */
const MIN_QUESTION = 5;
const DEBOUNCE_MS = 500;
const ANSWER_CACHE_MS = 10 * 60 * 1000;

const answerCache = new Map<string, { at: number; data: AssistantSearchResponse }>();

/** Can the panel point at this card's control — here, or on the page it is about to open? */
const canPointAt = (card: AssistantResultCard, pathname: string): boolean =>
  card.anchor !== null && (card.anchorRoute === card.deeplink || matchesRoute(card.anchorRoute, pathname));

const ResultCard = ({
  card,
  s,
  pathname,
  onNavigate,
}: {
  card: AssistantResultCard;
  s: HelpStrings;
  pathname: string;
  onNavigate: (card: AssistantResultCard) => void;
}) => {
  const onThisScreen = pathname === card.deeplink;
  const opens = card.anchor !== null && card.anchorAction === 'open';
  const offPage = card.anchor !== null && !canPointAt(card, pathname);
  const pointHere = card.anchor !== null && matchesRoute(card.anchorRoute, pathname);

  return (
    <div className="pct-help-ask-card">
      <div className="pct-help-ask-meta">
        <span className="pct-help-area-chip">{card.area}</span>
        {card.fallback && <span className="pct-help-ask-fallback">{s.askInEnglish}</span>}
      </div>
      <div className="pct-help-ask-job">{card.job}</div>
      <div className="pct-help-ask-desc">{card.description}</div>
      {card.prerequisites && (
        <div className="pct-help-ask-needs">
          {s.askNeeds}: {card.prerequisites}
        </div>
      )}
      {offPage && <div className="pct-help-ask-offpage">{s.askOnRecordPage}</div>}
      <div className="pct-help-ask-actions">
        {pointHere ? (
          <Button size="small" appearance="outline" icon={opens ? <Open16Regular /> : <Target16Regular />} onClick={() => onNavigate(card)}>
            {opens ? s.askOpenIt : s.askShowMe}
          </Button>
        ) : onThisScreen && offPage ? null : (
          <Button size="small" appearance="outline" icon={<ArrowRight16Regular />} iconPosition="after" onClick={() => onNavigate(card)}>
            {offPage ? s.askTakeMeToList : opens ? s.askTakeMeThereOpen : s.askTakeMeThere}
          </Button>
        )}
        <span className="pct-help-ask-id">{card.id}</span>
      </div>
    </div>
  );
};

interface HelpAskProps {
  language: HelpLanguage;
  /** Closes the drawer when the panel navigates away or hands over to a dialog. */
  onNavigated: () => void;
  /** Asks the drawer to stand aside while a control on THIS screen is pointed at. */
  onYield?: YieldHandler;
  /** Where a miss is reported — a toast, because pointing elsewhere closes this panel. */
  notify: (notice: HelpNotice) => void;
}

export const HelpAsk = ({ language, onNavigated, onYield, notify }: HelpAskProps) => {
  const s = HELP_STRINGS[language];
  const [question, setQuestion] = useState('');
  const [debounced, setDebounced] = useState('');
  const [data, setData] = useState<AssistantSearchResponse | null>(null);
  const [fetching, setFetching] = useState(false);
  const [failed, setFailed] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Deliberately NOT cancelled on unmount: navigating closes the drawer a tick after the flash
  // starts, and cancelling there swallowed the flash. A superseding flash cancels this one.
  const cancelFlash = useRef<(() => void) | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(question.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [question]);

  useEffect(() => setFeedbackSent(false), [debounced]);

  const enabled = debounced.length >= MIN_QUESTION;

  useEffect(() => {
    if (!enabled) {
      setData(null);
      setFailed(false);
      return undefined;
    }
    const key = JSON.stringify([debounced, language, pathname]);
    const hit = answerCache.get(key);
    if (hit && Date.now() - hit.at < ANSWER_CACHE_MS) {
      setData(hit.data);
      return undefined;
    }
    let live = true;
    setFetching(true);
    setFailed(false);
    searchAssistant({ question: debounced, screen: pathname, language, limit: 4 })
      .then((response) => {
        if (!live) return;
        // A repeated miss must be counted again, so only answers are cached.
        if (response.matchFound) answerCache.set(key, { at: Date.now(), data: response });
        setData(response);
      })
      .catch(() => {
        if (live) setFailed(true);
      })
      .finally(() => {
        if (live) setFetching(false);
      });
    return () => {
      live = false;
    };
  }, [enabled, debounced, language, pathname]);

  const cards = useMemo(() => data?.answer.entries ?? [], [data]);

  /** An offer the panel could not keep is withdrawn in words — never a silent no-op. */
  const reportMiss = (card: AssistantResultCard, reason: MissReason) => {
    const line = reason === 'behind-surface' ? s.askMissBehindSurface : s.askMissNotOnScreen;
    const message = [`${card.job} —`, line, card.prerequisites].filter(Boolean).join(' ');
    notify({ severity: reason === 'behind-surface' ? 'info' : 'warning', message });
  };

  const handleNavigate = (card: AssistantResultCard) => {
    cancelFlash.current?.();
    const activate = card.anchorAction === 'open';
    if (card.anchor && canPointAt(card, pathname)) {
      cancelFlash.current = flashAnchor(card.anchor, {
        activate,
        surface: card.anchorSurface,
        onYield,
        // The drawer sits above the modal layer, so a form opened under it is invisible.
        onHandover: onNavigated,
        onMissing: (reason) => reportMiss(card, reason),
      });
    }
    if (data?.queryId) {
      // An action, not a verdict — sent without `helpful`.
      void sendAssistantFeedback({ queryId: data.queryId, deeplinkFollowed: true }).catch(() => undefined);
    }
    const here = card.anchor !== null && matchesRoute(card.anchorRoute, pathname);
    if (here) {
      if (activate) onNavigated();
      return;
    }
    if (pathname !== card.deeplink) {
      navigate(card.deeplink);
      onNavigated();
    }
  };

  const handleFeedback = (helpful: boolean) => {
    if (!data?.queryId) return;
    setFeedbackSent(true);
    void sendAssistantFeedback({ queryId: data.queryId, helpful }).catch(() => undefined);
  };

  return (
    <div className="pct-help-ask">
      <div className="pct-help-ask-top">
        <div className="pct-help-ask-row">
          <Input
            autoFocus
            size="small"
            style={{ flex: 1 }}
            value={question}
            maxLength={300}
            aria-label={s.askPlaceholder}
            placeholder={s.askPlaceholder}
            contentBefore={<Search16Regular />}
            contentAfter={fetching ? <Spinner size="extra-tiny" /> : undefined}
            onChange={(_, d) => setQuestion(d.value)}
            data-help="help-ask-input"
          />
        </div>
        <p className="pct-help-hint" style={{ marginBottom: 0 }}>
          {s.askHint}
        </p>
      </div>

      <div className="pct-help-ask-results" data-help="help-ask-results">
        {!enabled && <p className="pct-help-muted">{s.askEmpty}</p>}
        {enabled && fetching && !data && <p className="pct-help-muted">{s.askThinking}</p>}
        {enabled && failed && <p className="pct-help-muted">{s.askError}</p>}

        {cards.map((card) => (
          <ResultCard key={card.id} card={card} s={s} pathname={pathname} onNavigate={handleNavigate} />
        ))}

        {data && !data.matchFound && (
          <div data-help="help-ask-not-found">
            <p className="pct-help-ask-desc">{data.notFound?.message}</p>
            {data.notFound?.suggestions && data.notFound.suggestions.length > 0 && (
              <>
                <p className="pct-help-muted" style={{ fontSize: 11.5 }}>
                  {s.askDidYouMean}
                </p>
                <div className="pct-help-chips">
                  {data.notFound.suggestions.map((suggestion) => (
                    <Button key={suggestion} size="small" shape="circular" appearance="outline" onClick={() => setQuestion(suggestion)}>
                      {suggestion}
                    </Button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {data && cards.length > 0 && (
          <div className="pct-help-feedback">
            <span>{feedbackSent ? s.askThanks : s.askHelpful}</span>
            {!feedbackSent && (
              <>
                <button type="button" className="pct-help-icon-button" title={s.askYes} aria-label={s.askHelpedAria} onClick={() => handleFeedback(true)}>
                  <ThumbLike16Regular />
                </button>
                <button
                  type="button"
                  className="pct-help-icon-button"
                  title={s.askNo}
                  aria-label={s.askNotHelpedAria}
                  onClick={() => handleFeedback(false)}
                >
                  <ThumbDislike16Regular />
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
